import { useCartStore } from '@/store/cart';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect, useRef } from 'react';
import { generateTimeSlots, formatTimeSlot, getKoreanTomorrow } from '@/lib/time';
import { RestaurantWithProducts } from '@/services/restaurant';
import { addMinutes } from '@/services/seller/discount/discount-time';
import { getTomorrowOperatingHours } from '@/lib/operatingHours';
import { useAuth } from '@/contexts/AuthContext';

interface CartStoreInfoProps {
  storeId: string;
  restaurantInfo?: RestaurantWithProducts | null;
  isLoading?: boolean;
}

export function CartStoreInfo({ storeId, restaurantInfo, isLoading = false }: CartStoreInfoProps) {
  const {
    storeName,
    orderType,
    pickupTime,
    pickupDate,
    desiredPickupTime,
    clearCart,
    setPickupTime,
    setDesiredPickupTime,
  } = useCartStore();
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 예약할인용 30분 단위 시간 슬롯 생성 (헬퍼 함수 사용)
  const availableTimeSlots = useMemo(() => {
    if (orderType !== 'RESERVATION_DISCOUNT' || !restaurantInfo || isLoading) return [];
    
    try {
      // 헬퍼 함수를 사용하여 내일 영업시간 계산
      const tomorrowHours = getTomorrowOperatingHours(restaurantInfo);
      
      if (tomorrowHours.isClosed || !tomorrowHours.openTime || !tomorrowHours.closeTime) {
        console.log('Restaurant is closed tomorrow or no operating hours');
        return [];
      }
      
      // 영업시작 1시간 후부터 시작
      const [openHour, openMinute] = tomorrowHours.openTime.split(':').map(Number);
      const pickupStartHour = openHour + 1;
      const pickupStartTime = `${String(pickupStartHour).padStart(2, '0')}:${String(openMinute).padStart(2, '0')}`;
      
      // 30분 단위 시간 슬롯 생성
      const timeSlots = generateTimeSlots(
        pickupStartTime, 
        tomorrowHours.closeTime, 
        tomorrowHours.breakStartTime, 
        tomorrowHours.breakEndTime
      );
      
      return timeSlots.map(slot => ({
        value: slot,
        label: formatTimeSlot(slot)
      }));
    } catch (error) {
      console.error('Error generating time slots:', error);
      return [];
    }
  }, [orderType, restaurantInfo, isLoading]);

  const getOrderTypeText = (type: string | null) => {
    switch (type) {
      case 'DEADLINE_DISCOUNT':
        return '마감할인';
      case 'RESERVATION_DISCOUNT':
        return '예약할인';
      case 'REGULAR_ORDER':
        return '일반주문';
      default:
        return '';
    }
  };

  const getPickupTimeLabel = () => {
    switch (orderType) {
      case 'DEADLINE_DISCOUNT':
        return '마감할인 수령시간:';
      case 'RESERVATION_DISCOUNT':
        return '예약수령 시간:';
      case 'REGULAR_ORDER':
        return '수령가능시간:';
      default:
        return '수령시간:';
    }
  };

  const formatHHMM = (time?: string | null) => {
    if (!time) return undefined;
    const [h = '00', m = '00'] = time.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const timeToMinutes = (time?: string | null) => {
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const getDeadlinePickupWindow = () => {
    if (orderType !== 'DEADLINE_DISCOUNT' || !restaurantInfo) return null;

    const today = new Date().toISOString().split('T')[0];
    const now = new Date();

    const offers = restaurantInfo.products
      ?.flatMap((p) => p.offers || [])
      .filter((offer) => offer.offer_type === 'DEADLINE_DISCOUNT' && offer.is_active)
      .filter((offer) => {
        if (offer.valid_from) {
          const offerDate = offer.valid_from.includes('T') ? offer.valid_from.split('T')[0] : offer.valid_from;
          if (offerDate !== today) return false;
        }
        if (offer.valid_until) {
          const validUntil = new Date(offer.valid_until);
          if (!Number.isNaN(validUntil.getTime()) && now > validUntil) return false;
        }
        return true;
      }) || [];

    const starts = offers
      .map((o) => formatHHMM(o.deadline_pickup_start_time))
      .filter(Boolean) as string[];
    const ends = offers
      .map((o) => formatHHMM(o.deadline_pickup_end_time))
      .filter(Boolean) as string[];

    if (starts.length > 0 && ends.length > 0) {
      const earliestStart = starts.reduce((earliest, cur) => {
        const curMin = timeToMinutes(cur) ?? Infinity;
        const prevMin = timeToMinutes(earliest) ?? Infinity;
        return curMin < prevMin ? cur : earliest;
      }, starts[0]);

      const latestEnd = ends.reduce((latest, cur) => {
        const curMin = timeToMinutes(cur) ?? -Infinity;
        const prevMin = timeToMinutes(latest) ?? -Infinity;
        return curMin > prevMin ? cur : latest;
      }, ends[0]);

      return { start: earliestStart, end: latestEnd };
    }

    if (restaurantInfo.deadline_discount_start_time && restaurantInfo.deadline_discount_end_time) {
      const pickupStart = formatHHMM(addMinutes(restaurantInfo.deadline_discount_start_time, 30));
      const pickupEnd = formatHHMM(restaurantInfo.deadline_discount_end_time);
      return pickupStart || pickupEnd ? { start: pickupStart, end: pickupEnd } : null;
    }

    return null;
  };

  const deadlineWindow = useMemo(() => getDeadlinePickupWindow(), [orderType, restaurantInfo, isLoading]);

  const handleStoreNavigate = () => {
    navigate(`/store/${storeId}`);
  };

  const handleClearCart = async () => {
    if (window.confirm('장바구니를 비우시겠습니까?')) {
      await clearCart(user?.id);
    }
  };

  // For reservation discount, show tomorrow's date
  const getTomorrowDate = () => {
    const tomorrow = getKoreanTomorrow();
    return tomorrow.toLocaleDateString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  // 시간 선택 핸들러
  const handleTimeSelect = (timeSlot: { value: string; label: string }) => {
    setDesiredPickupTime(timeSlot.value); // HH:MM 형식으로 저장
    setPickupTime({ start: timeSlot.label, end: timeSlot.label, value: timeSlot.value });
    setShowTimeDropdown(false);
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTimeDropdown(false);
      }
    };

    if (showTimeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTimeDropdown]);

  // 예약할인이고 시간 슬롯이 있으며 아직 시간이 선택되지 않았으면 기본값 설정
  useEffect(() => {
    if (orderType === 'RESERVATION_DISCOUNT' && 
        availableTimeSlots.length > 0 && 
        !pickupTime && 
        !desiredPickupTime) {
      const defaultSlot = availableTimeSlots[0];
      setDesiredPickupTime(defaultSlot.value);
      setPickupTime({ start: defaultSlot.label, end: defaultSlot.label, value: defaultSlot.value });
    }
  }, [availableTimeSlots, orderType, pickupTime, desiredPickupTime, setDesiredPickupTime, setPickupTime]);

  return (
    <div className="border-b-[3px] border-[#DEDEDE] pb-3 px-2">
      {/* Store Name & Delete */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-[#222222]">{storeName}</h2>
          <button
            onClick={handleStoreNavigate}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 5L15 12L9 19" stroke="#222222" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <button
          onClick={handleClearCart}
          className="p-1 bg-[#F5F5F5] rounded hover:bg-gray-200 transition-colors"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M3 6H21M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6H19Z" 
              stroke="#222222" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path d="M10 11V17M14 11V17" stroke="#222222" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Order Type */}
      <div className="mb-3">
        <span className="inline-block px-2 py-1 bg-[#DEDEDE] text-[#222222] rounded-lg text-base font-medium">
          {getOrderTypeText(orderType)}
        </span>
      </div>

      {/* Pickup Time */}
      <div className="mb-3">
        {orderType === 'DEADLINE_DISCOUNT' && restaurantInfo && (
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-[#222222]">{getPickupTimeLabel()}</span>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-base text-[#222222] whitespace-nowrap">
                {deadlineWindow?.start ? formatTimeSlot(deadlineWindow.start) : '오후 6시 30분'}
              </span>
              <span className="text-sm text-[#222222]">~</span>
              <span className="text-base text-[#222222] whitespace-nowrap">
                {deadlineWindow?.end ? formatTimeSlot(deadlineWindow.end) : '오후 8시 30분'}
              </span>
            </div>
          </div>
        )}
        {orderType === 'RESERVATION_DISCOUNT' && (
          <div>
            <span className="block text-base font-medium text-[#222222] mb-2">{getPickupTimeLabel()}</span>
            <div className="flex items-center gap-3 relative">
              <span className="text-base font-medium text-[#222222]">
                {pickupDate || getTomorrowDate()}
              </span>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  className="px-3 py-1 bg-[#F5F5F5] rounded-lg text-base text-[#222222] flex items-center gap-1 hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  <span className="whitespace-nowrap">
                    {pickupTime?.start || 
                     (availableTimeSlots.length > 0 ? availableTimeSlots[0].label : '시간 선택')}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="#222222" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                
                {/* 시간 선택 드롭다운 */}
                {showTimeDropdown && availableTimeSlots.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto min-w-[120px]">
                    {availableTimeSlots.map((slot, index) => (
                      <button
                        key={index}
                        onClick={() => handleTimeSelect(slot)}
                        className="w-full text-left px-3 py-2 text-base text-[#222222] hover:bg-gray-100 transition-colors first:rounded-t-lg last:rounded-b-lg whitespace-nowrap"
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {orderType === 'REGULAR_ORDER' && (
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-[#222222]">{getPickupTimeLabel()}</span>
            <span className="text-base text-[#222222] whitespace-nowrap">
              주문 즉시 ~ 준비 완료 후 2시간
            </span>
          </div>
        )}
      </div>

      {/* Pickup Method */}
      <div className="flex items-center gap-2">
        <span className="text-base font-medium text-[#222222]">수령 방식:</span>
        <div className="px-3 py-1 bg-[#F5F5F5] rounded-lg text-base font-medium text-[#222222]">
          <span>픽업</span>
        </div>
      </div>
    </div>
  );
}
